/**
 * Fixed Route Data Exporter
 * 
 * Extracts and transforms fixed route data from the drawer state into formats
 * compatible with UAT API uploads (createrouteversion and createshift).
 * 
 * This service handles:
 * - Extracting route metadata, shapes, stops, and timetable assignments
 * - Transforming drawer data format to UAT API schema
 * - Validating required fields are present
 * - Flagging missing data that needs user input
 */

import type { FixedRoute, FixedRouteStop, FixedRouteStopConfig, FixedRouteSegment, RouteGeometry } from '../types/fixedRoutes';

/**
 * UAT API payload structure for createrouteversion
 * Stops can be in one of two formats:
 * - Existing stop: { id: string, order, timeToNextStop, timeSpentAtStop }
 * - New stop: { label: string, address: {...}, order, timeToNextStop, timeSpentAtStop }
 */
export interface UATRouteVersionPayload {
  type: 'createrouteversion';
  version: '1';
  aclConfig: {
    resource: 'routes';
    operation: 'create';
  };
  aclContext: 'fixed_route';
  newRouteVersionData: {
    networkId: string;
    routeName: string;
    routeVersionName: string;
    routeId: string; // Always present. Empty string → new route; populated → new version of existing route.
    description: string;
    colour: string;
    routeShape: {
      // ODB expects an array of { latitude, longitude } objects, not [lat, lng] tuples.
      // Confirmed against hailer cypress/e2e/fixed-route/routes.cy.ts.
      coordinates: Array<{ latitude: number; longitude: number }>;
    };
    stops: Array<
      | {
          id: string;
          order: number;
          timeToNextStop: number; // minutes
          timeSpentAtStop: number; // minutes
        }
      | {
          label: string;
          address: {
            coordinates: {
              latitude: number;
              longitude: number;
            };
            label: string;
            placeId?: string;
          };
          order: number;
          timeToNextStop: number; // minutes
          timeSpentAtStop: number; // minutes
        }
    >;
  };
}

/**
 * UAT API payload structure for createshift
 */
export interface UATShiftPayload {
  type: 'createshift';
  version: '1';
  aclConfig: {
    resource: 'shifts';
    operation: 'create';
  };
  aclContext: 'fixed_route';
  newShiftData: {
    operationId: string;
    routeVersionId: string;
    vehicleId: string;
    driverId: string;
    // Aligned with the real Hailer contract: Hailer accepts only
    // 'forward' | 'reverse' (throws on the old 'backward').
    direction: 'forward' | 'reverse';
    endTime: string; // ISO datetime string
    startTime: string; // ISO datetime string
    numberOfRepeats: number;
    startStopIndex: number;
    shiftType: 'timetable' | 'shuttle'; // Hailer shift-solution types
    firstStopOffsetMins: number;
  };
}

/**
 * Extracted route data ready for UAT upload
 */
export interface ExportedRouteData {
  route: FixedRoute;
  routeMetadata: {
    name: string;
    description: string;
    colour: string;
  };
  routeShape: [number, number][]; // [[lat, lng], ...]
  stops: Array<{
    stopId: string; // UAT stop ID if available, otherwise Atlas stop ID
    stopName: string;
    order: number;
    timeToNextStop: number;
    timeSpentAtStop: number;
    coordinates: { lat: number; lng: number };
    hasUatStopId: boolean; // Flag indicating if this stop has a UAT stop ID
    atlasStopId?: string; // Original Atlas stop ID (for reference)
    address?: string; // Address label if known (used as `address.label` in NewStopPayload)
    placeId?: string; // Google place id if known (used as `address.placeId` in NewStopPayload)
    /**
     * Caller-defined postcode / external stop reference id carried over from
     * `FixedRouteStop.externalStopReferenceId`. Threaded into
     * `transformStopForPayload` so the createroutestop payload uses a
     * human-readable label (e.g. "Lake Road Health Centre") instead of falling
     * back to a coordinate pair when the stop has no UAT id and the upstream
     * `stopName` is a placeholder like "Stop 1".
     */
    externalStopReferenceId?: string;
  }>;
  missingData: {
    missingStops: string[]; // Stop IDs that don't exist in stops array
    missingShape: boolean;
    missingTimetableData: boolean; // True if shift/vehicle/driver assignments are missing
    stopsWithoutUatIds: string[]; // Atlas stop IDs that don't have UAT stop IDs
  };
}

/**
 * Validation result for route export
 */
export interface RouteExportValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  missingFields: {
    routeMetadata: string[];
    stops: string[];
    shape: boolean;
    timetable: boolean;
  };
}

/**
 * Extract complete route data from drawer state for UAT upload
 * 
 * @param route - The route to export
 * @param allStops - All available stops (to resolve stop references)
 * @returns Exported route data with validation flags
 */
export function exportRouteData(
  route: FixedRoute,
  allStops: FixedRouteStop[]
): ExportedRouteData {
  // Build stop lookup map
  const stopMap = new Map<string, FixedRouteStop>();
  allStops.forEach(stop => {
    stopMap.set(stop.id, stop);
  });

  // Extract route shape coordinates
  const routeShape: [number, number][] = route.geometry?.coordinates || [];

  // Process stops with order, timing, and validation
  const exportedStops: ExportedRouteData['stops'] = [];
  const missingStopIds: string[] = [];
  const stopsWithoutUatIds: string[] = [];

  route.stops.forEach((stopConfig, index) => {
    const stop = stopMap.get(stopConfig.stopId);
    
    if (!stop) {
      missingStopIds.push(stopConfig.stopId);
      return;
    }

    // Check if stop has UAT stop ID
    if (!stop.uatStopId) {
      stopsWithoutUatIds.push(stop.id);
    }

    // Calculate timeToNextStop from segments
    let timeToNextStop = 0;
    if (index < route.stops.length - 1) {
      const nextStopConfig = route.stops[index + 1];
      const segment = route.segments.find(
        seg => seg.fromStopId === stopConfig.stopId && seg.toStopId === nextStopConfig.stopId
      );
      if (segment) {
        // Apply traffic multiplier if available
        const multiplier = route.trafficMultiplier || 1.0;
        timeToNextStop = Math.round(segment.travelTimeMinutes * multiplier);
      }
    }

    exportedStops.push({
      stopId: stop.uatStopId || stop.id, // Use UAT stop ID if available, otherwise fall back to Atlas ID
      stopName: stop.name,
      order: index,
      timeToNextStop,
      timeSpentAtStop: stopConfig.waitTimeMinutes,
      coordinates: stop.coordinates,
      hasUatStopId: !!stop.uatStopId, // Flag to track if this stop has a UAT stop ID
      atlasStopId: stop.id, // Keep reference to original Atlas stop ID
      address: stop.address,
      placeId: stop.placeId,
      externalStopReferenceId: stop.externalStopReferenceId,
    });
  });

  return {
    route,
    routeMetadata: {
      name: route.name,
      description: '', // Not stored in FixedRoute - will need user input
      colour: route.color || '#000000',
    },
    routeShape,
    stops: exportedStops,
    missingData: {
      missingStops: missingStopIds,
      missingShape: routeShape.length === 0,
      missingTimetableData: true, // Routes don't store vehicle/driver/operation assignments
      stopsWithoutUatIds, // Stops that don't have UAT stop IDs
    },
  };
}

/**
 * Validate exported route data for UAT upload requirements
 * 
 * @param exportedData - The exported route data to validate
 * @returns Validation result with errors and warnings
 */
export function validateRouteExport(exportedData: ExportedRouteData): RouteExportValidation {
  const errors: string[] = [];
  const warnings: string[] = [];
  const missingFields = {
    routeMetadata: [] as string[],
    stops: [] as string[],
    shape: false,
    timetable: false,
  };

  // Validate route metadata
  if (!exportedData.routeMetadata.name) {
    errors.push('Route name is required');
    missingFields.routeMetadata.push('name');
  }

  // Validate stops
  if (exportedData.stops.length < 2) {
    errors.push('Route must have at least 2 stops');
  }

  exportedData.stops.forEach((stop, index) => {
    if (!stop.stopId) {
      errors.push(`Stop at order ${index} is missing stopId`);
      missingFields.stops.push(`order-${index}-stopId`);
    }
    if (stop.timeToNextStop < 0) {
      warnings.push(`Stop ${stop.stopName} has negative travel time to next stop`);
    }
    if (stop.timeSpentAtStop < 0) {
      warnings.push(`Stop ${stop.stopName} has negative wait time`);
    }
  });

  // Check for missing stops
  if (exportedData.missingData.missingStops.length > 0) {
    errors.push(
      `Route references ${exportedData.missingData.missingStops.length} stop(s) that don't exist: ${exportedData.missingData.missingStops.join(', ')}`
    );
  }

  // Stops without UAT stop IDs are sent as inline NewStopPayload entries by
  // transformStopForPayload — ODB createrouteversion accepts that format and
  // creates the stops as part of the route. Surface this as a warning so the
  // user knows new stops will be created, but don't block the upload.
  if (exportedData.missingData.stopsWithoutUatIds.length > 0) {
    const stopNames = exportedData.stops
      .filter(s => !s.hasUatStopId)
      .map(s => s.stopName)
      .join(', ');
    warnings.push(
      `${exportedData.missingData.stopsWithoutUatIds.length} stop(s) will be created in the network as part of this route: ${stopNames}`
    );
  }

  // Warn about missing shape (not blocking, but recommended)
  if (exportedData.missingData.missingShape) {
    warnings.push('Route shape (geometry) is missing - route will be created without visual path');
  }

  // Flag missing timetable data (shifts require vehicle/driver/operation assignments)
  if (exportedData.missingData.missingTimetableData) {
    warnings.push('Route does not have vehicle/driver/operation assignments - shifts cannot be created automatically');
    missingFields.timetable = true;
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    missingFields,
  };
}

/**
 * Validation result for a single stop
 */
export interface StopValidationError {
  stopId: string;
  stopName: string;
  missingFields: string[];
}

/**
 * Validation result for stops that will be created inline
 */
export interface StopsValidationResult {
  valid: boolean;
  errors: StopValidationError[];
}

/**
 * Validate stops for inline creation (required fields: name/label, coordinates)
 * 
 * @param stops - Stops to validate
 * @returns Validation result with errors for stops missing required fields
 */
export function validateStopsForInlineCreation(
  stops: Array<{
    id: string;
    name?: string;
    coordinates?: { lat: number; lng: number };
    address?: string;
  }>
): StopsValidationResult {
  const errors: StopValidationError[] = [];

  for (const stop of stops) {
    const missingFields: string[] = [];

    // Check required field: name or label (non-empty string)
    if (!stop.name || stop.name.trim() === '') {
      missingFields.push('name');
    }

    // Check required field: coordinates.lat (valid number, -90 to 90)
    if (
      !stop.coordinates ||
      typeof stop.coordinates.lat !== 'number' ||
      isNaN(stop.coordinates.lat) ||
      stop.coordinates.lat < -90 ||
      stop.coordinates.lat > 90
    ) {
      missingFields.push('coordinates.lat');
    }

    // Check required field: coordinates.lng (valid number, -180 to 180)
    if (
      !stop.coordinates ||
      typeof stop.coordinates.lng !== 'number' ||
      isNaN(stop.coordinates.lng) ||
      stop.coordinates.lng < -180 ||
      stop.coordinates.lng > 180
    ) {
      missingFields.push('coordinates.lng');
    }

    if (missingFields.length > 0) {
      errors.push({
        stopId: stop.id,
        stopName: stop.name || 'Unnamed Stop',
        missingFields,
      });
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Stop payload format for existing stops (with UAT stop ID)
 */
export interface ExistingStopPayload {
  id: string;
  order: number;
  timeToNextStop: number;
  timeSpentAtStop: number;
}

/**
 * Stop payload format for new stops (created inline)
 */
export interface NewStopPayload {
  label: string;
  address: {
    coordinates: {
      latitude: number;
      longitude: number;
    };
    label: string;
    placeId?: string;
  };
  order: number;
  timeToNextStop: number;
  timeSpentAtStop: number;
}

/**
 * Returns true if a candidate stop name looks like a "real" label vs. a
 * placeholder like "Stop 1" or a bare coordinate string. Used by the inline
 * stop builder to pick the best human-readable label.
 *
 * Heuristics, in order:
 *   - empty/whitespace-only: not real
 *   - "Stop <N>" / "Stop 1": placeholder
 *   - "<lat>, <lng>" coordinate strings: placeholder
 *   - otherwise: real
 */
export function isMeaningfulStopName(name: string | undefined | null): boolean {
  if (!name) return false;
  const trimmed = name.trim();
  if (!trimmed) return false;
  if (/^stop\s+\d+$/i.test(trimmed)) return false;
  // Coordinate-looking string e.g. "50.92085, -1.0176777" — accept optional
  // sign on either side, optional decimal portion, comma or whitespace
  // separator.
  if (/^-?\d+(?:\.\d+)?\s*,\s*-?\d+(?:\.\d+)?$/.test(trimmed)) return false;
  return true;
}

/**
 * Transform a stop for UAT route payload (handles both existing and new stops)
 *
 * @param stop - Stop data from exported route
 * @param uatStopId - UAT stop ID if available (from import or coordinate match)
 * @returns Stop payload in either existing or new stop format
 */
export function transformStopForPayload(
  stop: {
    stopId: string;
    stopName: string;
    order: number;
    timeToNextStop: number;
    timeSpentAtStop: number;
    coordinates: { lat: number; lng: number };
    address?: string;
    placeId?: string;
    /**
     * Optional postcode / external stop reference id carried over from the
     * Postcode Stop Assignment tool (the input sheet's `postcode_id` /
     * `stop_id` column, e.g. "SO31 4RA"). When set this is the preferred
     * human-readable label for the new inline stop's address.
     */
    externalStopReferenceId?: string;
  },
  uatStopId: string | null | undefined
): ExistingStopPayload | NewStopPayload {
  // If we have a UAT stop ID, use existing stop format
  if (uatStopId) {
    return {
      id: uatStopId,
      order: stop.order,
      timeToNextStop: stop.timeToNextStop,
      timeSpentAtStop: stop.timeSpentAtStop,
    };
  }

  // Otherwise, use new stop format (inline creation).
  //
  // ODB's Address model declares placeId as NOT NULL (see hailer
  // src/models/Address.ts:40-42), and NewStopPayload.address.placeId is typed
  // as `string` (required). If we omit it, the inline INSERT into addresses
  // violates the constraint and the whole createrouteversion call fails with
  // a generic "Creating new RouteVersion failed".
  //
  // For Atlas stops that didn't come from a Google Places lookup we
  // synthesise a stable placeId prefixed with `atlas:` so that
  // (a) repeat uploads of the same stop dedupe to the same Address row, and
  // (b) we never collide with real Google place ids (those start with ChIJ /
  // EI / GhIJ etc.).
  const placeId =
    stop.placeId && stop.placeId.trim().length > 0
      ? stop.placeId
      : `atlas:${stop.stopId}`;

  // Label hierarchy (2026-05-28; see tasks/postcode-improvements-2026-05-28.md):
  //   1. existing stopName when it's a real name (not "Stop 1" / bare coords)
  //   2. external_stop_reference_id from the postcode tool (e.g. resolved bus
  //      stop / hub name like "Lake Road Health Centre", or a raw postcode like
  //      "SO31 4RA" when no stop name was available)
  //   3. stop.address (legacy behaviour)
  //   4. "<lat>, <lng>" coordinate fallback
  //
  // Wiring update (2026-05-28, round 2 follow-up, Issue 2): the postcode →
  // FixedRouteStop bridge is now closed. The simulation activity importer
  // (`simulationResultsToFixedRoutes.ts`) walks `activity.trips[].ride` for
  // each activity, pulls `external_stop_reference_id`, and stashes it as
  // `FixedRouteStop.externalStopReferenceId`. `exportRouteData` propagates it
  // onto `ExportedRouteData['stops']` and `transformToUATRoutePayload` passes
  // it into this function — so step 2 of the hierarchy is live for postcode
  // pushes (provided the simulator echoes the field on its activity ride
  // payloads, which round 1 verified via the postcode UAT integration smoke).
  const fallbackLabel = `${stop.coordinates.lat}, ${stop.coordinates.lng}`;
  const preferredLabel = (() => {
    if (isMeaningfulStopName(stop.stopName)) return stop.stopName;
    if (stop.externalStopReferenceId && stop.externalStopReferenceId.trim()) {
      return stop.externalStopReferenceId.trim();
    }
    if (stop.address && stop.address.trim()) return stop.address;
    return fallbackLabel;
  })();

  return {
    label: preferredLabel,
    address: {
      coordinates: {
        latitude: stop.coordinates.lat,
        longitude: stop.coordinates.lng,
      },
      label: preferredLabel,
      placeId,
    },
    order: stop.order,
    timeToNextStop: stop.timeToNextStop,
    timeSpentAtStop: stop.timeSpentAtStop,
  };
}

/**
 * Resolve stop IDs for upload based on priority:
 * 1. Existing uatStopId (from import)
 * 2. Coordinate match (if user accepted)
 * 3. Inline creation (if no match or match rejected)
 * 
 * @param matches - Coordinate matching results
 * @param overrideDecisions - User override decisions (accept/reject)
 * @param existingStops - Existing stops with uatStopId property
 * @returns Map of Atlas stop ID → UAT stop ID (null if should be created inline)
 */
export function resolveStopIds(
  matches: Array<{
    atlasStopId: string;
    matchedUATStopId: string | null;
    withinTolerance: boolean;
  }>,
  overrideDecisions: Map<string, 'accept' | 'reject'>,
  existingStops: Array<{ id: string; uatStopId?: string }>
): Map<string, string | null> {
  const resolved = new Map<string, string | null>();

  // Create a map of existing stops by ID for quick lookup
  const stopsByAtlasId = new Map(
    existingStops.map(s => [s.id, s])
  );

  for (const match of matches) {
    const atlasStop = stopsByAtlasId.get(match.atlasStopId);
    
    // Priority 1: Use existing uatStopId if available
    if (atlasStop?.uatStopId) {
      resolved.set(match.atlasStopId, atlasStop.uatStopId);
      continue;
    }

    // Priority 2: Use coordinate match if user accepted
    if (match.withinTolerance && match.matchedUATStopId) {
      const decision = overrideDecisions.get(match.atlasStopId);
      if (decision === 'accept') {
        resolved.set(match.atlasStopId, match.matchedUATStopId);
        continue;
      }
      // If rejected, fall through to inline creation
    }

    // Priority 3: Mark for inline creation (no UAT stop ID)
    resolved.set(match.atlasStopId, null);
  }

  return resolved;
}

/**
 * Transform exported route data into UAT createrouteversion payload
 * 
 * @param exportedData - The exported route data
 * @param networkId - Target UAT network ID
 * @param routeVersionName - Version name for the route (e.g., "Version 1")
 * @param description - Route description (optional, defaults to empty)
 * @param resolvedStopIds - Map of Atlas stop ID → UAT stop ID (from coordinate matching or existing uatStopId)
 * @returns UAT API payload for createrouteversion
 */
export function transformToUATRoutePayload(
  exportedData: ExportedRouteData,
  networkId: string,
  routeVersionName: string = 'Version 1',
  description: string = '',
  resolvedStopIds?: Map<string, string | null>,
  existingRouteId?: string // Optional: Route ID of existing route when creating a new version
): UATRouteVersionPayload {
  // Transform stops using resolved stop IDs. ODB orders stops 1-indexed (per
  // the hailer cypress fixture); our internal export uses 0-based, so add 1.
  const stops = exportedData.stops.map(stop => {
    const uatStopId = stop.hasUatStopId ? stop.stopId : null;
    const resolvedUatStopId = resolvedStopIds?.get(stop.atlasStopId || stop.stopId);
    const finalUatStopId = resolvedUatStopId || uatStopId;

    return transformStopForPayload(
      {
        stopId: stop.atlasStopId || stop.stopId,
        stopName: stop.stopName,
        order: stop.order + 1, // 0-based → 1-based for ODB
        timeToNextStop: stop.timeToNextStop,
        timeSpentAtStop: stop.timeSpentAtStop,
        coordinates: stop.coordinates,
        address: stop.address,
        placeId: stop.placeId,
        externalStopReferenceId: stop.externalStopReferenceId,
      },
      finalUatStopId
    );
  });

  // Convert routeShape from [lat, lng] tuples to { latitude, longitude } objects.
  const routeShapeCoordinates = exportedData.routeShape.map(([lat, lng]) => ({
    latitude: lat,
    longitude: lng,
  }));

  // ODB's cypress fixture expects colour without a leading "#". Strip it
  // defensively (handles both `#6D4C41` and `6D4C41`).
  const normalisedColour = (exportedData.routeMetadata.colour || '').replace(/^#/, '');

  return {
    type: 'createrouteversion',
    version: '1',
    aclConfig: {
      resource: 'routes',
      operation: 'create',
    },
    aclContext: 'fixed_route',
    newRouteVersionData: {
      networkId,
      routeName: exportedData.routeMetadata.name,
      routeVersionName,
      // Mirror operations-portal exactly: routeId is always present.
      // Empty string → ODB creates a brand-new route; populated → ODB
      // creates a new version under the existing route (preserving shifts
      // and drivers, which reference routeId rather than versionId).
      routeId: existingRouteId ?? '',
      description: description || exportedData.routeMetadata.description,
      colour: normalisedColour,
      routeShape: {
        coordinates: routeShapeCoordinates,
      },
      stops: stops as Array<ExistingStopPayload | NewStopPayload>,
    },
  };
}

/**
 * Note: Shift creation requires additional data not stored in FixedRoute:
 * - operationId (operation/service ID)
 * - routeVersionId (returned from createrouteversion)
 * - vehicleId
 * - driverId
 * - direction ('forward' | 'reverse')
 * - startTime/endTime (ISO datetime strings)
 * - numberOfRepeats
 * - startStopIndex
 * - shiftType ('timetable' | 'shuttle')
 * - firstStopOffsetMins
 * 
 * These will need to be collected from user input or generated based on
 * route startTime/endTime and service schedule.
 */

